import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ensureMyProfile, getProfileByUsername, updateMyProfile } from '../services/profileService';
import { applyForMembership, getMyMembershipRequest, withdrawMembershipRequest } from '../services/membershipService';
import { MembershipRequest, ProfileSex, UserProfile } from '../types/profile';

type EditableField = 'username' | 'biography' | 'age' | 'sex';

const MAX_FILE_SIZE = 2 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const Profile: React.FC = () => {
  const { username: usernameParam } = useParams();
  const location = useLocation();
  const { user, loading: authLoading, uploadAvatar, updateUserMetadata } = useAuth();

  const isAccountRoute = location.pathname === '/account';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [editingField, setEditingField] = useState<EditableField | null>(null);

  const [usernameDraft, setUsernameDraft] = useState('');
  const [biographyDraft, setBiographyDraft] = useState('');
  const [ageDraft, setAgeDraft] = useState(25);
  const [sexDraft, setSexDraft] = useState<ProfileSex>('other');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Membership request state (only relevant for own profile)
  const [membershipRequest, setMembershipRequest] = useState<MembershipRequest | null>(null);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [applyMessage, setApplyMessage] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);

  const isOwnProfile = useMemo(() => {
    if (!profile || !user) {
      return false;
    }

    return profile.user_id === user.id;
  }, [profile, user]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    let active = true;

    const loadProfile = async () => {
      setLoading(true);
      setError('');
      setSuccessMessage('');

      if (!user) {
        setLoading(false);
        return;
      }

      if (isAccountRoute) {
        const { data, error: ensureError } = await ensureMyProfile(user);
        if (!active) {
          return;
        }

        if (ensureError || !data) {
          setError(ensureError?.message || 'Kunne ikke hente kontoen din.');
          setProfile(null);
        } else {
          setProfile(data);
          setUsernameDraft(data.username);
          setBiographyDraft(data.biography || '');
          setAgeDraft(data.age ?? 25);
          setSexDraft(data.sex ?? 'other');
          // Load membership request for own profile
          const { data: reqData } = await getMyMembershipRequest(user.id);
          if (active) setMembershipRequest(reqData);
        }

        setLoading(false);
        return;
      }

      if (!usernameParam) {
        setError('Profilen finnes ikke.');
        setLoading(false);
        return;
      }

      const { data, error: fetchError } = await getProfileByUsername(usernameParam);
      if (!active) {
        return;
      }

      if (fetchError || !data) {
        setError('Fant ikke denne profilen.');
        setProfile(null);
      } else {
        setProfile(data);
        setUsernameDraft(data.username);
        setBiographyDraft(data.biography || '');
        setAgeDraft(data.age ?? 25);
        setSexDraft(data.sex ?? 'other');
      }

      setLoading(false);
    };

    loadProfile();

    return () => {
      active = false;
    };
  }, [authLoading, isAccountRoute, usernameParam, user]);

  const startEdit = (field: EditableField) => {
    if (!profile) {
      return;
    }

    setSuccessMessage('');
    if (field === 'username') {
      setUsernameDraft(profile.username);
    }
    if (field === 'biography') {
      setBiographyDraft(profile.biography || '');
    }
    if (field === 'age') {
      setAgeDraft(profile.age ?? 25);
    }
    if (field === 'sex') {
      setSexDraft(profile.sex ?? 'other');
    }

    setEditingField(field);
  };

  const cancelEdit = () => {
    setEditingField(null);
    setError('');
  };

  const saveField = async (field: EditableField) => {
    if (!user || !profile || !isOwnProfile) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    const updates: Record<string, string | number | null> = {};

    if (field === 'username') {
      const cleaned = usernameDraft.trim();
      if (cleaned.length < 3) {
        setError('Brukernavn må være minst 3 tegn.');
        setSaving(false);
        return;
      }
      updates.username = cleaned;
    }

    if (field === 'biography') {
      updates.biography = biographyDraft.trim() || null;
    }

    if (field === 'age') {
      updates.age = ageDraft;
    }

    if (field === 'sex') {
      updates.sex = sexDraft;
    }

    const { data, error: updateError } = await updateMyProfile(user.id, updates);

    if (updateError || !data) {
      setError(updateError?.message || 'Kunne ikke lagre feltet.');
      setSaving(false);
      return;
    }

    setProfile(data);
    setEditingField(null);
    setSaving(false);
    setSuccessMessage('Profilen er oppdatert.');
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file || !user || !profile || !isOwnProfile) {
      return;
    }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Velg et gyldig bilde (JPEG, PNG, GIF eller WebP).');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('Bildet må være mindre enn 2 MB.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    const { url, error: uploadError } = await uploadAvatar(user.id, file);

    if (uploadError || !url) {
      setError(uploadError || 'Kunne ikke laste opp profilbildet.');
      setSaving(false);
      return;
    }

    const { data, error: updateError } = await updateMyProfile(user.id, {
      avatar_url: url,
    });

    if (updateError || !data) {
      setError(updateError?.message || 'Kunne ikke lagre profilbildet.');
      setSaving(false);
      return;
    }

    setProfile(data);
    await updateUserMetadata({ avatar_url: url });
    setSaving(false);
    setSuccessMessage('Profilbildet er oppdatert.');
  };

  const sexLabel = (value: ProfileSex | null) => {
    if (value === 'male') {
      return 'Male';
    }
    if (value === 'female') {
      return 'Female';
    }
    if (value === 'other') {
      return 'Other';
    }
    return 'Ikke satt';
  };

  const joinedLabel = useMemo(() => {
    if (!profile?.created_at) {
      return '';
    }

    const date = new Date(profile.created_at);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('nb-NO', {
      year: 'numeric',
      month: 'long',
    }).format(date);
  }, [profile?.created_at]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center px-4">
        <div className="text-lg" style={{ color: 'rgba(255,255,255,0.4)' }}>Laster profil...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] text-white py-14 relative overflow-hidden">
      <div className="absolute -top-24 -right-16 w-72 h-72 pointer-events-none" />
      <div className="absolute bottom-16 -left-20 w-80 h-80 pointer-events-none" />

      <div className="container mx-auto px-4 max-w-5xl relative z-10">
        {error && (
          <div className="mb-6 bg-red-900 bg-opacity-30 border border-red-500 border-opacity-50 text-red-300 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-6 bg-emerald-900 bg-opacity-30 border border-emerald-500 border-opacity-40 text-emerald-200 px-4 py-3 rounded-xl text-sm">
            {successMessage}
          </div>
        )}

        {!profile ? (
          <div className="bg-zinc-900/70 rounded-2xl p-8">
            <p className="text-gray-300">Fant ikke profilen.</p>
          </div>
        ) : (
          <div className="space-y-10">
            <header>
              <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight">
                {profile.username}
              </h1>
              <p className="text-gray-400 mt-2 text-responsive">
                {isOwnProfile ? 'Administrer profilen din.' : 'Profil synlig for innloggede brukere.'}
              </p>
              <div className="mt-4 flex flex-wrap gap-2 text-sm">
                <span className="px-3 py-1 rounded-full bg-zinc-900/80 text-gray-300">@{profile.username}</span>
                {joinedLabel && (
                  <span className="px-3 py-1 rounded-full bg-zinc-900/80 text-gray-300">Medlem siden {joinedLabel}</span>
                )}
                <span className="px-3 py-1 rounded-full bg-zinc-900/80 text-gray-300">{isOwnProfile ? 'Din profil' : 'Offentlig visning'}</span>
              </div>
            </header>

            <section className="pt-2">
              <div className="flex flex-col md:flex-row gap-8 md:items-start">
                <div className="relative w-36 h-36 shrink-0">
                  {profile.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={profile.username}
                      className="w-36 h-36 rounded-2xl object-cover" style={{ border: '1px solid rgba(255,255,255,0.12)' }}
                    />
                  ) : (
                    <div className="w-36 h-36 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <span className="text-4xl text-gray-500">?</span>
                    </div>
                  )}

                  {isOwnProfile && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-white text-black flex items-center justify-center"
                      title="Endre profilbilde"
                      disabled={saving}
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
                      </svg>
                    </button>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleAvatarUpload}
                  />
                </div>

                <div className="flex-1 space-y-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="w-full">
                      <p className="text-sm text-gray-400 mb-1">Username</p>
                      {editingField === 'username' ? (
                        <input
                          type="text"
                          value={usernameDraft}
                          onChange={(event) => setUsernameDraft(event.target.value)}
                          className="input-glow w-full md:w-80 px-4 py-2"
                          maxLength={32}
                        />
                      ) : (
                        <p className="text-2xl font-semibold text-white">@{profile.username}</p>
                      )}
                    </div>
                    {isOwnProfile && (
                      editingField === 'username' ? (
                        <div className="flex gap-2">
                          <button type="button" onClick={cancelEdit} className="btn-secondary py-2 px-4 text-sm" disabled={saving}>Avbryt</button>
                          <button type="button" onClick={() => saveField('username')} className="btn-primary py-2 px-4 text-sm" disabled={saving}>Lagre</button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => startEdit('username')} className="link-primary" title="Rediger username">
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
                          </svg>
                        </button>
                      )
                    )}
                  </div>

                  <p className="text-gray-400 text-sm">Profilinformasjon</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="p-4 bg-black/40 rounded-lg">
                      <div className="flex items-start justify-between gap-3">
                        <div className="w-full">
                          <p className="text-sm text-gray-400 mb-1">Age</p>
                          {editingField === 'age' ? (
                            <div>
                              <input
                                type="range"
                                min={13}
                                max={100}
                                value={ageDraft}
                                onChange={(event) => setAgeDraft(Number(event.target.value))}
                                className="w-full accent-emerald-500"
                              />
                              <p className="text-white mt-1 font-semibold opacity-80">{ageDraft}</p>
                            </div>
                          ) : (
                            <p className="text-white font-semibold">{profile.age ?? 'Ikke satt'}</p>
                          )}
                        </div>
                        {isOwnProfile && (
                          editingField === 'age' ? (
                            <div className="flex gap-2">
                              <button type="button" onClick={cancelEdit} className="btn-secondary py-1.5 px-3 text-xs" disabled={saving}>Avbryt</button>
                              <button type="button" onClick={() => saveField('age')} className="btn-primary py-1.5 px-3 text-xs" disabled={saving}>Lagre</button>
                            </div>
                          ) : (
                            <button type="button" onClick={() => startEdit('age')} className="link-primary" title="Rediger age">
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
                              </svg>
                            </button>
                          )
                        )}
                      </div>
                    </div>

                    <div className="p-4 bg-black/40 rounded-lg">
                      <div className="flex items-start justify-between gap-3">
                        <div className="w-full">
                          <p className="text-sm text-gray-400 mb-1">Sex</p>
                          {editingField === 'sex' ? (
                            <select
                              value={sexDraft}
                              onChange={(event) => setSexDraft(event.target.value as ProfileSex)}
                              className="input-glow w-full px-3 py-2"
                            >
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="other">Other</option>
                            </select>
                          ) : (
                            <p className="text-white font-semibold">{sexLabel(profile.sex)}</p>
                          )}
                        </div>
                        {isOwnProfile && (
                          editingField === 'sex' ? (
                            <div className="flex gap-2">
                              <button type="button" onClick={cancelEdit} className="btn-secondary py-1.5 px-3 text-xs" disabled={saving}>Avbryt</button>
                              <button type="button" onClick={() => saveField('sex')} className="btn-primary py-1.5 px-3 text-xs" disabled={saving}>Lagre</button>
                            </div>
                          ) : (
                            <button type="button" onClick={() => startEdit('sex')} className="link-primary" title="Rediger sex">
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
                              </svg>
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="pt-2">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h2 className="text-2xl font-bold text-white tracking-tight">Biography</h2>
                {isOwnProfile && (
                  editingField === 'biography' ? (
                    <div className="flex gap-2">
                      <button type="button" onClick={cancelEdit} className="btn-secondary py-2 px-4 text-sm" disabled={saving}>Avbryt</button>
                      <button type="button" onClick={() => saveField('biography')} className="btn-primary py-2 px-4 text-sm" disabled={saving}>Lagre</button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => startEdit('biography')} className="link-primary" title="Rediger biography">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
                      </svg>
                    </button>
                  )
                )}
              </div>

              {editingField === 'biography' ? (
                <textarea
                  value={biographyDraft}
                  onChange={(event) => setBiographyDraft(event.target.value)}
                  className="input-glow w-full px-4 py-3 min-h-[180px]"
                  maxLength={500}
                />
              ) : (
                <p className="text-gray-200 whitespace-pre-wrap leading-relaxed text-lg md:text-xl max-w-4xl">
                  {profile.biography || 'Ingen biografi enda.'}
                </p>
              )}
            </section>

            {/* Membership status — only shown on own account page */}
            {isOwnProfile && profile.role !== 'admin' && (
              <section className="pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <h2 className="text-xl font-bold text-white mb-4 tracking-tight">Smugler-medlemskap</h2>

                {profile.role === 'smugler' ? (
                  <div className="flex items-center gap-3 px-4 py-3 bg-emerald-950/40 border border-emerald-500 border-opacity-30 rounded-xl">
                    <span className="text-emerald-400 text-xl">✓</span>
                    <div>
                      <p className="text-emerald-300 font-semibold">Du er Smugler</p>
                      <p className="text-gray-400 text-sm">Du kan publisere og redigere blogginnlegg.</p>
                    </div>
                  </div>
                ) : membershipRequest === null ? (
                  // No request submitted yet
                  showApplyForm ? (
                    <div className="space-y-3 p-4" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                      <p className="text-sm text-gray-300">
                        Fortell kort om deg selv og hvorfor du ønsker å bli Smugler:
                      </p>
                      <textarea
                        value={applyMessage}
                        onChange={(e) => setApplyMessage(e.target.value)}
                        placeholder="Skriv litt om deg selv..."
                        rows={4}
                        maxLength={500}
                        className="input-glow w-full px-4 py-3 resize-none text-sm"
                      />
                      <p className="text-gray-600 text-xs text-right">{applyMessage.length}/500</p>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => { setShowApplyForm(false); setApplyMessage(''); }}
                          className="btn-secondary py-2 px-4 text-sm"
                          disabled={applyLoading}
                        >
                          Avbryt
                        </button>
                        <button
                          type="button"
                          disabled={applyLoading || !applyMessage.trim()}
                          className="btn-primary py-2 px-5 text-sm disabled:opacity-50"
                          onClick={async () => {
                            if (!user || !applyMessage.trim()) return;
                            setApplyLoading(true);
                            const { data } = await applyForMembership(user.id, applyMessage.trim());
                            setMembershipRequest(data);
                            setShowApplyForm(false);
                            setApplyMessage('');
                            setApplyLoading(false);
                          }}
                        >
                          {applyLoading ? 'Sender...' : 'Send søknad'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-4 px-4 py-3 bg-black/40 rounded-xl">
                      <div>
                        <p className="text-gray-300 font-medium">Bli en Smugler</p>
                        <p className="text-gray-500 text-sm">Søk om medlemskap og få tilgang til å publisere innlegg.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowApplyForm(true)}
                        className="btn-primary py-2 px-5 text-sm shrink-0"
                      >
                        Søk nå
                      </button>
                    </div>
                  )
                ) : membershipRequest.status === 'pending' ? (
                  <div className="flex items-start justify-between gap-4 px-4 py-3 bg-yellow-950/30 border border-yellow-600 border-opacity-30 rounded-xl">
                    <div>
                      <p className="text-yellow-300 font-semibold">⏳ Søknad til behandling</p>
                      <p className="text-gray-400 text-sm mt-0.5">En admin vil gjennomgå søknaden din snart.</p>
                      {membershipRequest.message && (
                        <p className="text-gray-500 text-xs mt-2 italic">&ldquo;{membershipRequest.message}&rdquo;</p>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={applyLoading}
                      className="text-xs text-gray-500 hover:text-red-400 transition-colors shrink-0"
                      onClick={async () => {
                        if (!user) return;
                        setApplyLoading(true);
                        await withdrawMembershipRequest(user.id);
                        setMembershipRequest(null);
                        setApplyLoading(false);
                      }}
                    >
                      Trekk tilbake
                    </button>
                  </div>
                ) : membershipRequest.status === 'denied' ? (
                  <div className="space-y-3">
                    <div className="px-4 py-3 bg-red-950/30 border border-red-600 border-opacity-30 rounded-xl">
                      <p className="text-red-300 font-semibold">✕ Søknad avslått</p>
                      <p className="text-gray-400 text-sm mt-0.5">Du kan sende en ny søknad når som helst.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowApplyForm(true)}
                      className="btn-secondary py-2 px-5 text-sm"
                    >
                      Send ny søknad
                    </button>
                  </div>
                ) : null}
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
